import { ApolloLink } from '@apollo/client';
import { EMPTY, of } from 'rxjs';
import { map } from 'rxjs/operators';
// keep track of the queries that are not completed
// similar to opQueue in QueueLink library since the original is private
/**
 * Keeps track of the queries that are not completed in offline mode
 */
class QueueLinkTracker extends ApolloLink {
  private queue: ApolloLink.Operation[] = [];
  //Using map instead of array for faster lookup
  private isClose = false;
  /**
   * This function is called when the link is executed, in other words whenever a query is made.
   * It keeps track of the queries that are not completed in offline mode.
   * If the link is closed, the query is added to the queue, and the request is sent back with a dummy response.
   * If the link is closed and the query is executed, it is removed from the queue.
   * @updated 4/04/2024 - 08:41:56
   * @param operation The operation or query that is to be executed
   * @param forward  The next link in the chain, if there is one
   */
  request(
    operation: ApolloLink.Operation,
    forward?: ApolloLink.ForwardFunction,
  ) {
    if (forward === undefined) {
      return EMPTY;
    }

    if (this.isClose) {
      this.queue.push(operation);

      // If this occurred during offline mode, just return a dummy response
      // tailored to the operation. The legacy `InsertDataFromForm` callers
      // ignore the shape, but the new `SubmitFormAnswers` callers branch on
      // `data.submitFormAnswers.success` — returning a `success: true`
      // dummy lets the optimistic UI continue while the real mutation
      // remains queued for replay.
      if (operation.operationName === 'InsertDataFromForm') {
        const fetchResult: ApolloLink.Result = {
          data: {
            insertDataFromForm: {
              projectId: '',
              __typename: 'Data',
            },
          },
          extensions: { offlineQueued: true },
        };
        return of(fetchResult);
      }
      if (operation.operationName === 'SubmitFormAnswers') {
        const fetchResult: ApolloLink.Result = {
          data: {
            submitFormAnswers: {
              success: true,
              error: null,
              __typename: 'SubmitFormAnswersResponse',
            },
          },
          extensions: { offlineQueued: true },
        };
        return of(fetchResult);
      }
    }

    return forward(operation).pipe(
      map((data) => {
        this.queue = this.queue.filter((item: any) => item !== operation);
        return data;
      }),
    );
  }

  close(): void {
    this.isClose = true;
  }

  open(): void {
    this.isClose = false;
  }

  getQueue(): ApolloLink.Operation[] {
    return this.queue;
  }
}

export const offlineLinkTracker = new QueueLinkTracker();
